/**
 * geo.js — canonical coordinate parsing + validation for the map components.
 *
 * Mirrors backend lib/orderLogic.js so the customer/admin/trucker maps and the
 * server agree on exactly which coordinates may be plotted. The state guard is
 * the fix for the "Phoenix order plotted in San Francisco" defect: the old
 * North-America box accepted any US coordinate, so a mis-geocoded AZ site in CA
 * still showed a pin.
 */

export function parseLocation(data) {
  if (data === null || data === undefined) return null;
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;

  if (!Array.isArray(parsed) && (parsed.lat !== undefined || parsed.latitude !== undefined)) {
    const lat = parseFloat(parsed.lat ?? parsed.latitude);
    const lng = parseFloat(parsed.lng ?? parsed.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return null;
  }
  if (!Array.isArray(parsed)) return null;

  if (parsed.length >= 2 && !Array.isArray(parsed[0]) && typeof parsed[0] !== "object") {
    const lat = Number(parsed[0]);
    const lng = Number(parsed[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return null;
  }
  if (parsed.length > 0 && Array.isArray(parsed[0])) {
    return centroid(parsed.map((p) => (Array.isArray(p) ? { lat: Number(p[0]), lng: Number(p[1]) } : null)));
  }
  if (parsed.length > 0 && typeof parsed[0] === "object") {
    return centroid(parsed.map((p) => ({ lat: Number(p.lat ?? p.latitude), lng: Number(p.lng ?? p.longitude) })));
  }
  return null;
}

export function centroid(points) {
  let sumLat = 0, sumLng = 0, n = 0;
  for (const p of points) {
    if (p && !Number.isNaN(p.lat) && !Number.isNaN(p.lng)) { sumLat += p.lat; sumLng += p.lng; n++; }
  }
  return n ? { lat: sumLat / n, lng: sumLng / n } : null;
}

export function isValidNorthAmericaCoord(c) {
  if (!c || typeof c.lat !== "number" || typeof c.lng !== "number") return false;
  if (Number.isNaN(c.lat) || Number.isNaN(c.lng)) return false;
  if (c.lat === 0 && c.lng === 0) return false;
  return c.lat >= 15 && c.lat <= 72 && c.lng >= -170 && c.lng <= -50;
}

export const STATE_BBOX = {
  AZ: { latMin: 31.3, latMax: 37.1, lngMin: -114.9, lngMax: -109.0 },
  NV: { latMin: 35.0, latMax: 42.1, lngMin: -120.1, lngMax: -114.0 },
  CA: { latMin: 32.5, latMax: 42.1, lngMin: -124.5, lngMax: -114.1 },
  UT: { latMin: 37.0, latMax: 42.1, lngMin: -114.1, lngMax: -109.0 },
};

export function normalizeState(state) {
  if (!state) return null;
  return String(state).trim().toUpperCase().slice(0, 2);
}

export function coordinateMatchesState(coord, state) {
  const box = STATE_BBOX[normalizeState(state)];
  if (!box || !isValidNorthAmericaCoord(coord)) return false;
  return (
    coord.lat >= box.latMin && coord.lat <= box.latMax &&
    coord.lng >= box.lngMin && coord.lng <= box.lngMax
  );
}

/**
 * RockPros only operates in AZ / UT / NV / CA. Any order coordinate outside
 * these states is almost certainly a bad geocode or a mistyped client address,
 * so the maps raise a cross-check alert instead of silently plotting it.
 */
export const SERVICE_STATES = ["AZ", "UT", "NV", "CA"];

export function isInServiceArea(coord) {
  if (!isValidNorthAmericaCoord(coord)) return false;
  return SERVICE_STATES.some((s) => coordinateMatchesState(coord, s));
}

/**
 * Checks every coordinate attached to an order (delivery site, quarry, live
 * truck fix) against the service area. Returns [] when all good, otherwise
 * one entry per offending coordinate: { label, coord }.
 */
export function serviceAreaViolations({ delivery, pickup, truck } = {}) {
  const violations = [];
  const check = (label, coord) => {
    if (coord && !isInServiceArea(coord)) violations.push({ label, coord });
  };
  check("delivery site", delivery);
  check("pickup / quarry", pickup);
  check("truck location", truck);
  return violations;
}

/** Returns {plot, reason}. expectedState optional (the order's delivery state). */
export function shouldPlotCoordinate(coord, expectedState) {
  if (!isValidNorthAmericaCoord(coord)) return { plot: false, reason: "invalid-or-off-continent" };
  if (expectedState && !coordinateMatchesState(coord, expectedState)) {
    return { plot: false, reason: `outside-expected-state:${normalizeState(expectedState)}` };
  }
  return { plot: true, reason: "ok" };
}
